import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { CustomerAddressPanel } from '../components/CustomerAddressPanel.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { formatOwners } from '../lib/owners.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { STATEMENTS_TEMPLATES } from '../email-templates/templates.js';
import { useContentMap } from '../content.js';

/**
 * SCRN 38 — Statements Menu (CCF-28) and SCRN 39 — statement sub-flows.
 *
 * Faithful port of the Figma "Statements" flow. The agent picks an account
 * (name + last 4 digits) — optionally including closed accounts — sees the
 * account details, then a "Statement options" dropdown of four options
 * (CCF-28), each backed by an R4 gateway op:
 *   1. Up to date statements   — CCF-74: `statement/order`.
 *   2. Duplicate statements    — CCF-75: `statement/dates` (date-range order,
 *                                €2.50/page max €20 — scripting-gated).
 *   3. Amend statement details — CCF-29: `statement/details` (READ, pre-fill)
 *                                + `statement/details/amend` (write).
 *   4. Interest certificate    — CCF-38: `certificates/list` (READ years) +
 *                                `certificate/order` (order).
 *
 * Backed by the Teller-ID-gated BFF via the generated typed client:
 *   - accounts:  POST /api/customers/accounts
 *   - details:   POST /api/accounts/details      (IBAN / balance / statement info)
 *   - order:     POST /api/accounts/statement-order
 *   - duplicate: POST /api/accounts/statement-duplicate
 *   - amend read/write: POST /api/accounts/statement-details(-amend)
 *   - certificates: POST /api/accounts/certificates(/-order)
 *
 * Every gateway-calling action surfaces the gateway's own Code + Message inline
 * beside its button on failure (#80/#82) and a positive confirmation on success.
 *
 * The shared agent banner lives in the app shell, so this screen renders only
 * the `<main>` content.
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Status?: string;
}

/** Account detail fields this screen consumes (the gateway returns more). */
interface AccountDetail {
  Found?: boolean;
  AvailableBalance?: number;
  /** ISO currency code from `customer/accounts/list` — EUR, USD, GBP, … (#415). */
  Currency?: string;
  Iban?: string;
  LastStatementDate?: string;
  NextStatementDate?: string;
  // #283: the real gateway sends this as a NUMBER (numeric cycle code); the
  // screen's label helper coerces it, so the type reflects both wire forms.
  StatementFrequency?: string | number;
  CustomerHoldIndicator?: string;
  /**
   * CCF-28: the account owners. The gateway may return an array of names, a
   * single string, or omit it. Rendered gracefully (falls back to the banner
   * customer name when absent).
   */
  Owners?: string[] | string;
  AccountOwners?: string[] | string;
  /**
   * #459: whether the IDENTIFIED caller is the PRIMARY holder of this account.
   * The gateway `customer/accounts/list` association carries this per account as
   * a Y/N flag (scoped to the caller's CIF); rendered as Yes / No. A boolean is
   * also tolerated. Distinct from Owners, which lists ALL account holders
   * (#414).
   */
  PrimaryAccountHolder?: string | boolean;
  /**
   * CCF-74 statement indicators (optional). The gateway may carry these as a
   * boolean or a Y/N string; rendered as Yes / No / "—" when absent.
   */
  EStatement?: boolean | string;
  GreenStatement?: boolean | string;
}

/** Normalises a possibly-boolean / Y-N / missing flag to a display label. */
function flagLabel(value: boolean | string | number | undefined): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // #283: the real PTSB gateway can send a flag/code as a NON-STRING (e.g. a
  // number). A bare `.trim()` on a number throws and — with the raw value
  // reaching this helper during render — crashed the whole screen. Coerce first.
  const s = String(value).trim().toLowerCase();
  if (s === 'y' || s === 'yes' || s === 'true') return 'Yes';
  if (s === 'n' || s === 'no' || s === 'false') return 'No';
  return String(value);
}

/** True when a Y/N (or boolean) gateway flag is opted-in. */
function flagOn(value: boolean | string | undefined): boolean {
  if (typeof value === 'boolean') return value;
  const s = (value ?? '').trim().toLowerCase();
  return s === 'y' || s === 'yes' || s === 'true';
}

/**
 * Renders ALL account owners from the detail shape, or '' when not present.
 * #414: delegates to the shared `formatOwners` helper so joint holders are
 * listed comma-separated identically to every other owner-displaying surface
 * (Dashboard #11 / Transactions #382 / Account Information / Chequebook) —
 * previously this screen rendered the ampersand-joined string ("A & B") raw,
 * inconsistent with the rest of the app.
 */
function ownersLabel(detail: AccountDetail | null): string {
  return formatOwners(detail?.Owners ?? detail?.AccountOwners);
}

/** The statement-order outcome the BFF returns (gateway Code + Message). */
interface StatementOrderResult {
  Success?: boolean;
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * Issues #178 / #180 — the documented `ErrorNumber`s the statement-order ops
 * (`statement/dates` for the Duplicate Statements date-range order, and the
 * shared `statement/order`) decline with, mapped to a clear, agent-readable
 * reason. The gateway's own `ErrorMessage` for every decline is the generic
 * "Unable to process the transaction", so the bare numeric code (e.g. "9313 —
 * Unable to process the transaction") is what reached the agent — unreadable to
 * a customer. The reasons below are the authoritative meanings from the R4
 * reference (`requirements/unibanks-reference/Unibanks_OrderAccountStatement_DateRange.yaml`
 * + `Unibanks_StatementOrder.yaml`), NOT a guess: e.g. #178 reported 9313 as an
 * "insufficient funds" decline, but per the reference 9313 actually means there
 * are no transactions in the chosen range — surfacing the accurate reason keeps
 * the agent correctly informed. Mirrors the #80/#82/#94/#167 pattern
 * (PostalAddressScreen / FundsTransferScreen `TRANSFER_CODE_REASON`).
 */
const STATEMENT_ORDER_CODE_REASON: Record<string, string> = {
  // Date-range declines (statement/dates — Duplicate Statements, CCF-75).
  '9299': 'The From date cannot be later than today.',
  '9300': 'The dates must fall within the available statement history.',
  '9301': 'The From date is earlier than the account opening date.',
  '9304': 'The account was closed before the From date.',
  '9305': 'Only one reprint per day is allowed for this account.',
  '9306': 'No statement history was found for this account.',
  '9309': 'The To date cannot be later than today.',
  '9310': 'The account was closed before the To date.',
  '9311': 'The To date cannot be earlier than the From date.',
  '9312': 'The From date cannot be later than the To date.',
  '9313': 'No transactions were found for the selected date range.',
  // Order declines shared with the up-to-date statement order (statement/order).
  '9221': 'A bank control record could not be found for this account.',
  '9267': 'A hold on the account is preventing the statement order.',
  '9268': 'A statement order is not permitted for this account type.',
};

/**
 * Issue #197 — the AUTHORITATIVE `certificate/order` (CCF-38) decline catalogue
 * supplied by @dylan219 (2026-07-01; also a CSV in Drive). These are legitimate
 * BUSINESS declines the app must MAP + DISPLAY as a clear, agent-readable reason
 * — NOT a gateway defect. As with statement orders, the gateway's own
 * `ErrorMessage` is the generic "Unable to Process Transaction" for every
 * decline, so the bare numeric code (e.g. "9295 — Unable to Process
 * Transaction") is all the agent saw before this map (the reported symptom).
 *
 * NB #162 previously confirmed 9295 is a GENUINE business decline (for a
 * different account) and correctly relayed it verbatim; this map does not
 * reverse that — it adds the client-side MEANING for the relayed code so the
 * agent understands WHY, per Dylan's #197 ruling. Distinct from the
 * statement-order map above (`certificate/order` has its own code space, e.g.
 * `662`/`9295`/`9296` which are not statement-order codes). Unknown codes fall
 * back to the raw Code + gateway message (never swallowed).
 */
const CERT_ORDER_CODE_REASON: Record<string, string> = {
  '42': 'The certificate year (date) is invalid.',
  '97': 'A certificate of interest is not available for this account.',
  '100': 'The account could not be found.',
  '153': 'The channel is closed. Please try again later.',
  '154': 'The channel is invalid. Please try again later.',
  '289': 'This account is not linked to the selected customer.',
  '505': 'The teller (Zuser) could not be found.',
  '662': 'A certificate for this period is already on order.',
  '9101': 'The customer record could not be found.',
  '9102': 'The card is not valid for this customer.',
  '9103': 'The online card number is invalid.',
  '9104': 'The card type is not valid for the online card.',
  '9295':
    'This account earned no interest for the selected period, so a certificate cannot be ordered.',
  '9296': 'Certificates cannot be ordered for SSIA adjustments.',
};

/**
 * Issue #478 — the AUTHORITATIVE `statement/details/amend` (CCF-29, SCRN-38)
 * decline catalogue, taken verbatim from the R4 reference
 * (`requirements/unibanks-reference/API_Error_Codes.csv`,
 * `Unibanks_AmendStatementDetails.yaml`). These are legitimate PTSB-gateway
 * BUSINESS declines tied to the account's product type — NOT a request-shape
 * defect. As with the statement-order / certificate maps, the gateway's own
 * `ErrorMessage` for a decline is the generic "Unable to Process Transaction",
 * so before this map the agent only saw a bare numeric code (the #478 symptom:
 * ticking "Issue statement at end of month (EOM)" "throws" a 984 / 975 with no
 * explanation of why). #478 confirmed the reporter's exact codes against the
 * catalogue for a cheque/OD-style account:
 *   - 984 ("Keep Statement at EOM must be 'N'") — EOM='Y' is not permitted for
 *     this account type, so it declines at every frequency (month/3/6-month).
 *   - 975 ("Max STMT FREQ for Cheque/OD A/Cs is 6 Months") — a 12-month
 *     frequency exceeds the 6-month cap for a cheque/overdraft account.
 * The amend op has its OWN code space (975/976/977/979-982/984/9187) distinct
 * from the statement-order map, hence a dedicated catalogue. Unknown codes fall
 * back to the raw Code + gateway message (never swallowed).
 */
const AMEND_STATEMENT_CODE_REASON: Record<string, string> = {
  '100': 'The account could not be found.',
  '153': 'The channel is closed. Please try again later.',
  '154': 'The channel is invalid. Please try again later.',
  '289': 'This account is not linked to the selected customer.',
  '504': 'The account type could not be resolved (Batyp not found).',
  '828': 'The teller entered is invalid.',
  '936': 'This account is not active.',
  '975':
    'The maximum statement frequency for cheque/overdraft accounts is 6 months. Choose a shorter frequency.',
  '976': 'The maximum statement frequency is 12 months.',
  '977':
    'The frequency or cycle cannot be updated for this account type (a maturity option is set).',
  '979': 'The statement date entered is invalid.',
  '980': 'The next statement date must be in the future.',
  '981': 'The next statement date must be within one year.',
  '982':
    'There are pending charges, so the next statement date cannot be changed.',
  '984':
    'This account type does not support issuing at end of month (EOM). Untick "Issue statement at end of month" and retry.',
  '9101': 'The customer record could not be found.',
  '9102': 'The card is not valid for this customer.',
  '9103': 'The online card number is invalid.',
  '9104': 'The card type is not valid for the online card.',
  '9187': 'The next statement date cannot be amended for this account type.',
};

/**
 * Issue #80 / #178 / #180 / #197 / #478 — build the inline failure text for the
 * Order Statement / Order Certificate / Amend actions. Surfaces the gateway's
 * own Code and — when the numeric code is a KNOWN `ErrorNumber` — appends the
 * authoritative human-readable reason, since the gateway's message is the
 * generic "Unable to process the transaction" for every decline so the bare
 * code is all the agent would otherwise see. Pass `certificate: true` from the
 * interest-certificate flow (its own `certificate/order` code space, #197) or
 * `amend: true` from the amend-details flow (the `statement/details/amend` code
 * space, #478); the statement-order map is used otherwise. Falls back to the
 * gateway's verbatim message, then a generic line, when no mapped reason exists
 * (so an unmapped/new code is never swallowed). `0000` is the internal success
 * code and is never shown.
 */
export function formatOrderError(
  result: StatementOrderResult | undefined,
  opts?: { certificate?: boolean; amend?: boolean },
): string {
  const code = result?.ResultCode?.trim();
  const message = result?.ResultMessage?.trim();
  // Issue #185 — the 3-hold BLOCK is an APP decision (the gateway is never
  // called), carried as the non-numeric `THREE_HOLD` code + the mandated
  // advisory. Show the advisory verbatim with no code prefix (it is not a
  // gateway ErrorNumber and the copy is self-explanatory).
  if (code === 'THREE_HOLD' && message) return message;
  // Issue #509 — a FAILED holds read (banking service down) blocks the order but
  // is NOT a code-3 hold. The BFF carries the honest, self-explanatory advisory
  // as the non-numeric `HOLD_CHECK_FAILED` code; show it verbatim (no code prefix
  // — it is not a gateway ErrorNumber), letting the agent retry.
  if (code === 'HOLD_CHECK_FAILED' && message) return message;
  const showCode = code && code !== '0000';
  const map = opts?.certificate
    ? CERT_ORDER_CODE_REASON
    : opts?.amend
      ? AMEND_STATEMENT_CODE_REASON
      : STATEMENT_ORDER_CODE_REASON;
  const reason = showCode ? map[code] : undefined;
  if (showCode && reason) return `${code} — ${reason}`;
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `Order failed (code ${code}).`;
  if (opts?.certificate)
    return 'Could not order the interest certificate. Please retry.';
  if (opts?.amend)
    return 'Could not amend the statement details. Please retry.';
  return 'Could not order the statement. Please retry.';
}

/** The four Statement Options (CCF-28). */
const STATEMENT_OPTIONS = [
  { value: 'up-to-date', label: 'Up to date statements' },
  { value: 'duplicate', label: 'Duplicate statements' },
  { value: 'amend', label: 'Amend statement details' },
  { value: 'interest', label: 'Interest certificate' },
] as const;

type StatementOption = (typeof STATEMENT_OPTIONS)[number]['value'] | '';

/**
 * Statement frequency options (CCF-29). The label is what the agent picks; the
 * value is the PADDED 3-digit cycle code the gateway `statement/details/amend`
 * op writes back (001/003/006/012, per CCF-29). The READ op (`statement/
 * details`) may return the value unpadded (e.g. "1"/"3"/"12"); `padFrequency`
 * normalises a read value back onto these padded codes so the pre-fill selects
 * the right option AND the amend write always sends the padded write code.
 */
const FREQUENCY_OPTIONS = [
  { value: '001', label: 'Every Month' },
  { value: '003', label: 'Every 3 Months' },
  { value: '006', label: 'Every 6 Months' },
  { value: '012', label: 'Every 12 Months' },
] as const;

// CCF-29 (#461): the original EOM sentinel was '00/00/0000' (Unibanks
// recalculate value). This was changed in #516: the staging gateway returns 984
// when EOM=Y + NextStatementDate='00/00/0000' on some account types. The screen
// now sends the last day of the current month instead (see
// ddmmyyyyLastDayOfCurrentMonth). The BFF still normalises a blank date to
// '00/00/0000' as defence-in-depth for direct callers (#461 Error.Code 10006).

/**
 * CCF-29: normalise a statement-frequency value read from the gateway onto the
 * padded 3-digit write code (001/003/006/012). The READ op may return the cycle
 * unpadded ("1"/"3"/"6"/"12") or already padded; either way we map it back to
 * the padded write code so the dropdown pre-selects correctly and the amend
 * write sends the CCF-29 write code rather than the raw read value.
 */
function padFrequency(value: string | number | undefined): string {
  // #283: coerce before string ops — the gateway sends the cycle as a number.
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  // Strip leading zeros to compare numerically, then re-pad to 3 digits.
  const numeric = trimmed.replace(/^0+/, '') || '0';
  if (/^\d+$/.test(numeric)) return numeric.padStart(3, '0');
  return trimmed;
}

/**
 * CCF-74/75 (#226): map the statement-frequency value the gateway returns —
 * the numeric cycle code (`1`/`3`/`6`/`12`, or the padded `001`/`003`/…)
 * threaded through `/accounts/details` from `statement/details` — onto the
 * human-readable label CCF-75 mandates ("Every Month", "Every 3 Months", …).
 * A non-numeric label (e.g. the gateway already sent "Monthly"/"Annual") is
 * shown verbatim; a blank/absent value renders "—" so the read-only view never
 * surfaces a bare code the agent can't interpret.
 */
function frequencyLabel(value: string | number | undefined): string {
  // #283: the real gateway's `account/information` sends `StatementFrequency` as
  // a NUMBER (the numeric cycle code, e.g. 1). Coerce before string ops so a raw
  // number renders as its mapped label ("Every Month") instead of crashing.
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '—';
  const padded = padFrequency(trimmed);
  const match = FREQUENCY_OPTIONS.find((o) => o.value === padded);
  return match ? match.label : trimmed;
}

/**
 * Date conversion helpers for the Amend "Next due date" field (#158).
 * State (and the gateway payload) keep the DD/MM/CCYY format the Bank API
 * expects; the native `<input type="date">` value is ISO `YYYY-MM-DD`, so we
 * convert at the UI boundary only. Both helpers are total: anything that is
 * blank, "00/00/0000", or not a real calendar date yields '' rather than
 * throwing.
 */
function ddmmyyyyToIso(value: string | undefined): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value ?? '').trim());
  if (!m) return '';
  const [, dd, mm, yyyy] = m;
  const iso = `${yyyy}-${mm}-${dd}`;
  // Reject impossible dates (e.g. 00/00/0000, 31/02) by round-tripping.
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  if (
    d.getFullYear() !== Number(yyyy) ||
    d.getMonth() + 1 !== Number(mm) ||
    d.getDate() !== Number(dd)
  ) {
    return '';
  }
  return iso;
}

function isoToDdmmyyyy(value: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!m) return '';
  const [, yyyy, mm, dd] = m;
  return `${dd}/${mm}/${yyyy}`;
}

/** Today's date as an ISO `YYYY-MM-DD` string (local time, date only). */
function todayIso(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * True when a DD/MM/CCYY date is a valid date strictly before today (#159).
 * Compares the calendar date only (not time). Blank / invalid dates are not
 * treated as "past" (the gateway-format guard handles those separately).
 */
function isBeforeToday(ddmmyyyy: string | undefined): boolean {
  const iso = ddmmyyyyToIso(ddmmyyyy);
  if (!iso) return false;
  return iso < todayIso();
}

/** True when a DD/MM/CCYY string is a real calendar date (round-trips). */
function isValidDdmmyyyy(value: string | undefined): boolean {
  return ddmmyyyyToIso(value) !== '';
}

/** Last day of the current month in `DD/MM/YYYY` for the EOM statement-amend sentinel (#516). */
function ddmmyyyyLastDayOfCurrentMonth(): string {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const dd = String(last.getDate()).padStart(2, '0');
  const mm = String(last.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${last.getFullYear()}`;
}

/**
 * CCF-75 (#179): per-field validation for the Duplicate Statements date range.
 * The agent types the From/To boxes free-text (DD/MM/YYYY), so a letter, a
 * malformed string, a future date or a From later than To must be caught
 * client-side and surfaced INLINE next to the offending box — never as a bare
 * generic "please retry" after a gateway round-trip. Returns the per-field
 * messages ('' = valid); the empty-field case is handled by the disabled button
 * (so a blank box never shows a scary error before the agent has typed).
 */
export function validateDuplicateDates(
  from: string,
  to: string,
): { from: string; to: string } {
  const errors = { from: '', to: '' };
  const fromTrim = (from ?? '').trim();
  const toTrim = (to ?? '').trim();
  const today = todayIso();

  // Validate each box that has been typed into. A valid date must be a real
  // calendar date in DD/MM/YYYY form (catches letters, partial/garbage input)
  // and cannot be in the future (a statement can only cover the past).
  if (fromTrim) {
    if (!isValidDdmmyyyy(fromTrim)) {
      errors.from = 'Enter a valid date in DD/MM/YYYY format.';
    } else if (ddmmyyyyToIso(fromTrim) > today) {
      errors.from = 'From date cannot be in the future.';
    }
  }
  if (toTrim) {
    if (!isValidDdmmyyyy(toTrim)) {
      errors.to = 'Enter a valid date in DD/MM/YYYY format.';
    } else if (ddmmyyyyToIso(toTrim) > today) {
      errors.to = 'To date cannot be in the future.';
    }
  }

  // When both boxes hold valid dates, the range must run forwards. Flag the To
  // box (the one the agent most likely needs to raise) when From is after To.
  if (
    !errors.from &&
    !errors.to &&
    isValidDdmmyyyy(fromTrim) &&
    isValidDdmmyyyy(toTrim) &&
    ddmmyyyyToIso(fromTrim) > ddmmyyyyToIso(toTrim)
  ) {
    errors.to = 'To date must be on or after the From date.';
  }

  return errors;
}

/** Current statement details for the Amend pre-fill (CCF-29). */
interface StatementDetails {
  Found?: boolean;
  NextStatementDate?: string;
  LastStatementDate?: string;
  StatementCycle?: string;
  StatementFrequency?: string;
  StatementEOM?: string;
  GreenStatement?: string;
  ResultCode?: string;
  ResultMessage?: string;
}

/** A certificate row available to order (CCF-38). */
interface CertificateRow {
  YearEnding: string;
  CreditInterest: string;
  DirtTax: string;
}

/** The certificates-list outcome the BFF returns (CCF-38). */
interface CertificateList {
  Found?: boolean;
  AnnualCertRequired?: string;
  Certificates?: CertificateRow[];
  ResultCode?: string;
  ResultMessage?: string;
}

/** Years shown per page in the certificate picker (CCF-38: groups of 5). */
const CERT_PAGE_SIZE = 5;

/**
 * Format an amount in its account currency (or '—' when absent). Uses the
 * platform currency formatter so a foreign-currency account renders the correct
 * symbol (USD → US$, GBP → £, …), not a hardcoded € (issue #415). Defaults to
 * EUR and falls back to a code-prefixed string for an unrecognised ISO code.
 */
function money(amount: number | undefined, currency = 'EUR'): string {
  if (amount === undefined || Number.isNaN(amount)) return '—';
  const code = currency || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function StatementsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);
  const contentMap = useContentMap();

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  // CCF-74 CIF Address Validation: the customer's CIF address on file, sourced
  // from the customer dashboard detail set (the same source the Personal Details
  // screen uses). The five address lines and the Eircode are kept apart so the
  // shared CustomerAddressPanel renders them the same way Card Maintenance does
  // (CCF-77) — labelled Line 1–5 + an Eircode row.
  const [cifAddress, setCifAddress] = useState<string[]>([]);
  const [cifEircode, setCifEircode] = useState('');
  // CCF-74: the account's postal/delivery address on file (the same per-account
  // source the Postal Address screen reads — SCRN-23 / `getPostalAddress`),
  // held as its five lines for the side-by-side panel. Non-fatal to load.
  const [postalAddress, setPostalAddress] = useState<string[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [option, setOption] = useState<StatementOption>('');

  // Up to date order (CCF-74) state.
  const [scriptingComplete, setScriptingComplete] = useState(false);
  const [ordered, setOrdered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Issue #80: a failed/declined order must ALWAYS surface a persistent inline
  // error next to the action button — a transient toast alone is not enough.
  const [orderError, setOrderError] = useState('');

  // Duplicate statements (CCF-75) state.
  const [dupFrom, setDupFrom] = useState('');
  const [dupTo, setDupTo] = useState('');
  const [dupScripting, setDupScripting] = useState(false);
  const [dupOrdered, setDupOrdered] = useState(false);
  const [dupSubmitting, setDupSubmitting] = useState(false);
  const [dupError, setDupError] = useState('');
  // #179: per-field date validation errors — surfaced INLINE next to the
  // offending From/To box (with aria-invalid) so the agent sees exactly which
  // box holds bad input, rather than a generic "please retry" after submit.
  const [dupFromError, setDupFromError] = useState('');
  const [dupToError, setDupToError] = useState('');

  // Amend statement details (CCF-29) state — pre-filled from statement/details.
  const [amendDetails, setAmendDetails] = useState<StatementDetails | null>(
    null,
  );
  const [amendLoading, setAmendLoading] = useState(false);
  const [amendFrequency, setAmendFrequency] = useState('');
  const [amendNextDate, setAmendNextDate] = useState('');
  const [amendEom, setAmendEom] = useState(false);
  const [amendGreen, setAmendGreen] = useState(false);
  const [amendOrdered, setAmendOrdered] = useState(false);
  const [amendSubmitting, setAmendSubmitting] = useState(false);
  const [amendError, setAmendError] = useState('');

  // Interest certificate (CCF-38) state.
  const [certList, setCertList] = useState<CertificateList | null>(null);
  const [certLoading, setCertLoading] = useState(false);
  const [certSelected, setCertSelected] = useState<Set<string>>(new Set());
  const [certPage, setCertPage] = useState(0);
  const [certAnnual, setCertAnnual] = useState(false);
  const [certOrdered, setCertOrdered] = useState(false);
  const [certSubmitting, setCertSubmitting] = useState(false);
  const [certError, setCertError] = useState('');
  // Distinct from `certError` (the order error) so the Update action's own
  // gateway Code + Message surfaces inline beside the Update button (#80/#82).
  const [certUpdateError, setCertUpdateError] = useState('');

  const [error, setError] = useState('');
  // Monotonic sequence so a slow earlier detail load can't overwrite a newer one.
  const detailSeq = useRef(0);
  // Same guard for the per-option sub-flow reads (amend pre-fill / certificates):
  // an in-flight response for a previously-selected account must not populate the
  // form once the agent has switched account/option.
  const amendSeq = useRef(0);
  const certSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // CCF-74 CIF Address Validation: load the CIF address on file so the agent can
  // confirm it before ordering. Reuses the customer dashboard endpoint (the same
  // detail set the Personal Details screen reads). Non-fatal: the CIF column
  // renders blank lines if it can't load, never blocking the statement order.
  useEffect(() => {
    let cancelled = false;
    async function loadCifAddress(): Promise<void> {
      if (!hasContext) return;
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled || apiError || !data) return;
        const info = (data.info ?? {}) as Record<string, unknown>;
        const str = (v: unknown): string =>
          typeof v === 'string' ? v.trim() : '';
        setCifAddress([
          str(info.CustomerAddressLine1),
          str(info.CustomerAddressLine2),
          str(info.CustomerAddressLine3),
          str(info.CustomerAddressLine4),
          str(info.CustomerAddressLine5),
        ]);
        setCifEircode(str(info.CustomerEircode));
      } catch {
        // Non-fatal — the CIF address column renders blank lines when absent.
      }
    }
    void loadCifAddress();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  /** Accounts shown in the dropdown — open only, unless "Show closed" is ticked. */
  const visibleAccounts = accounts.filter(
    (a) => showClosed || a.Status !== 'CLOSED',
  );

  /** Load the selected account's detail (IBAN / balance / statement info). */
  async function loadDetail(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    const seq = ++detailSeq.current;
    setDetailLoading(true);
    setError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/details', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      if (seq !== detailSeq.current) return;
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : 'Could not load the account details. Please retry.',
        );
        return;
      }
      setDetail(data as unknown as AccountDetail);
    } catch {
      if (seq === detailSeq.current) setError('Request failed.');
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }

  /**
   * CCF-74: load the account's postal/delivery address on file so the agent can
   * confirm it before ordering — the same per-account source (and shape:
   * Line1–Line5) the Postal Address screen reads (`getPostalAddress`, granted to
   * exactly the same roles as the statement order). Non-fatal: the postal column
   * renders blank when none is on file, never blocking the order.
   */
  async function loadPostalAddress(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    // Share the detail sequence so a slow response for a previously-selected
    // account can't overwrite the newer account's postal address (the agent
    // confirms it before ordering — it must stay bound to the selected account).
    const seq = detailSeq.current;
    try {
      const { data, error: apiError } = await api.POST(
        '/accounts/postal-address',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber },
        },
      );
      if (seq !== detailSeq.current) return;
      if (apiError || !data) return;
      const addr = data as {
        Found?: boolean;
        Line1?: string;
        Line2?: string;
        Line3?: string;
        Line4?: string;
        Line5?: string;
      };
      if (!addr.Found) {
        setPostalAddress([]);
        return;
      }
      setPostalAddress(
        [addr.Line1, addr.Line2, addr.Line3, addr.Line4, addr.Line5].map((v) =>
          typeof v === 'string' ? v.trim() : '',
        ),
      );
    } catch {
      // Non-fatal — the postal column renders blank when it can't load.
    }
  }

  /** Reset every sub-flow's transient state (on account or option change). */
  function resetFlows(): void {
    setScriptingComplete(false);
    setOrdered(false);
    setOrderError('');
    setDupFrom('');
    setDupTo('');
    setDupScripting(false);
    setDupOrdered(false);
    setDupError('');
    setDupFromError('');
    setDupToError('');
    setAmendDetails(null);
    setAmendFrequency('');
    setAmendNextDate('');
    setAmendEom(false);
    setAmendGreen(false);
    setAmendOrdered(false);
    setAmendError('');
    setCertList(null);
    setCertSelected(new Set());
    setCertPage(0);
    setCertAnnual(false);
    setCertOrdered(false);
    setCertSubmitting(false);
    setCertError('');
    setCertUpdateError('');
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    setDetail(null);
    setOption('');
    resetFlows();
    setPostalAddress([]);
    void loadDetail(value);
    void loadPostalAddress(value);
  }

  function onOptionChange(value: string): void {
    setOption(value as StatementOption);
    resetFlows();
    // The Amend + Interest Certificate flows load their data lazily on select.
    if (value === 'amend') void loadStatementDetails(selectedAccount);
    if (value === 'interest') void loadCertificates(selectedAccount);
  }

  /**
   * CCF-29: load the current statement details and PRE-FILL all four amendable
   * fields (frequency / next due date / green-statement / EOM) with the current
   * values from Unibanks (#86/#87). A gateway business error (Found:false) is
   * surfaced inline rather than a silently-blank form.
   */
  async function loadStatementDetails(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    const seq = ++amendSeq.current;
    setAmendLoading(true);
    setAmendError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/statement-details', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      // Ignore a stale response for a previously-selected account/option.
      if (seq !== amendSeq.current) return;
      if (apiError || !data) {
        // Issue #248/#303: `orderStatement` gates this READ too (it pre-fills
        // the Amend sub-flow), so an offline agent 403s here — before ever
        // reaching the Amend button. RBAC action-denial (`{ error: 'forbidden' }`)
        // → @dylan219's authoritative verbatim inline copy; the Teller-ID 403
        // (`{ error: 'invalid_teller_id' }`) keeps its own distinct message.
        setAmendError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : 'Could not load the statement details. Please retry.'),
        );
        return;
      }
      const d = data as StatementDetails;
      if (d.Found === false) {
        setAmendError(
          formatOrderError(d as StatementOrderResult, { amend: true }),
        );
        return;
      }
      setAmendDetails(d);
      // Pre-fill (#86/#87): frequency code, next due date, EOM + green flags.
      // CCF-29: normalise the (possibly unpadded) read frequency onto the
      // padded 001/003/006/012 write code so the dropdown pre-selects.
      setAmendFrequency(padFrequency(d.StatementFrequency));
      const prefilledEom = flagOn(d.StatementEOM);
      const prefilledDate = d.NextStatementDate ?? '';
      setAmendNextDate(
        prefilledEom && (!prefilledDate || prefilledDate === '00/00/0000')
          ? ddmmyyyyLastDayOfCurrentMonth()
          : prefilledDate,
      );
      setAmendEom(prefilledEom);
      setAmendGreen(flagOn(d.GreenStatement));
    } catch {
      if (seq === amendSeq.current) setAmendError('Request failed.');
    } finally {
      if (seq === amendSeq.current) setAmendLoading(false);
    }
  }

  /**
   * CCF-38: load the interest certificates available to order (years the account
   * was open) + the automated-interest-cert flag. A gateway business error
   * (Found:false) is surfaced inline.
   */
  async function loadCertificates(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    const seq = ++certSeq.current;
    setCertLoading(true);
    setCertError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/certificates', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      // Ignore a stale response for a previously-selected account/option.
      if (seq !== certSeq.current) return;
      if (apiError || !data) {
        // Issue #248/#303: `orderStatement` gates this READ too (it lists the
        // Interest Certificate sub-flow's orderable years), so an offline
        // agent 403s here — before ever reaching the Order button. RBAC
        // action-denial (`{ error: 'forbidden' }`) → @dylan219's authoritative
        // verbatim inline copy; the Teller-ID 403 (`{ error: 'invalid_teller_id' }`)
        // keeps its own distinct message.
        setCertError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : 'Could not load the interest certificates. Please retry.'),
        );
        return;
      }
      const list = data as CertificateList;
      if (list.Found === false) {
        setCertError(
          formatOrderError(list as StatementOrderResult, { certificate: true }),
        );
        return;
      }
      setCertList(list);
      setCertAnnual(flagOn(list.AnnualCertRequired));
    } catch {
      if (seq === certSeq.current) setCertError('Request failed.');
    } finally {
      if (seq === certSeq.current) setCertLoading(false);
    }
  }

  async function orderStatement(): Promise<void> {
    setSubmitting(true);
    setOrderError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/statement-order', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber: selectedAccount },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #80: surface the failure as a PERSISTENT inline error next to
        // the action button (the toast alone auto-dismisses and is easy to
        // miss). Show the gateway's own Code + Message verbatim (e.g. "100 —
        // Unable to Process Transaction", or the CCF-74 3-hold warning) so the
        // agent can act on the real reason — never a bare generic popup.
        // Issue #248: an RBAC *action* denial (`requirePermission` refuses the
        // blocked write with 403 `{ error: 'forbidden' }`) surfaces @dylan219's
        // authoritative verbatim copy inline. Any OTHER 403 (e.g. the Teller-ID
        // gate's `{ error: 'invalid_teller_id' }`) keeps its own distinct message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatOrderError(data as StatementOrderResult | undefined));
        setOrderError(message);
        toast.error(message);
        return;
      }
      setOrdered(true);
      // Distinct from the inline "Statement ordered" confirmation so the two
      // never collide in tests (the inline message is the faithful Figma copy).
      toast.success('Statement ordered successfully.');
    } catch {
      const message = 'Could not order the statement. Please retry.';
      setOrderError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * #179: validate the From/To date boxes, write the per-field inline errors,
   * and return true when the range is valid + both boxes filled. Used on submit
   * (and re-runnable on blur) so invalid input is caught BEFORE the gateway.
   */
  function validateDupDates(): boolean {
    const fieldErrors = validateDuplicateDates(dupFrom, dupTo);
    setDupFromError(fieldErrors.from);
    setDupToError(fieldErrors.to);
    return (
      !fieldErrors.from && !fieldErrors.to && !!dupFrom.trim() && !!dupTo.trim()
    );
  }

  /** CCF-75: order a duplicate statement for the entered date range. */
  async function orderDuplicate(): Promise<void> {
    // #179: catch invalid/garbage/out-of-order dates client-side and highlight
    // the offending box inline, rather than letting the gateway reject the call
    // and showing the generic "Could not order the statement. Please retry."
    if (!validateDupDates()) {
      return;
    }
    setDupSubmitting(true);
    setDupError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/statement-duplicate', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          dateFrom: dupFrom,
          dateTo: dupTo,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248: RBAC action-denial → verbatim inline copy; the Teller-ID
        // 403 keeps its own distinct message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatOrderError(data as StatementOrderResult | undefined));
        setDupError(message);
        toast.error(message);
        return;
      }
      setDupOrdered(true);
      toast.success('Duplicate statement ordered successfully.');
    } catch {
      const message = 'Could not order the duplicate statement. Please retry.';
      setDupError(message);
      toast.error(message);
    } finally {
      setDupSubmitting(false);
    }
  }

  /** CCF-29: amend the statement details with the form values. */
  async function amendStatement(): Promise<void> {
    // #159: block a past Next Due Date before touching the gateway. EOM greys
    // the field (sent as the recalculate sentinel for Unibanks), so skip the
    // check then. Surface the reason inline next to the action button so the
    // agent can correct the date and retry (the authoritative inline-error UX
    // rule).
    if (!amendEom && isBeforeToday(amendNextDate)) {
      const message = "Next due date cannot be before today's date.";
      setAmendError(message);
      toast.error(message);
      return;
    }
    setAmendSubmitting(true);
    setAmendError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/statement-details-amend', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          // #516: last day of current month is required when EOM is set.
          nextStatementDate: amendNextDate,
          statementCycle: amendDetails?.StatementCycle || 'MONTHLY',
          // CCF-29: always send the padded 001/003/006/012 write code.
          statementFrequency: padFrequency(amendFrequency),
          statementEOM: amendEom ? 'Y' : 'N',
          greenStatement: amendGreen ? 'Y' : 'N',
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248: RBAC action-denial → verbatim inline copy; the Teller-ID
        // 403 keeps its own distinct message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          (response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : formatOrderError(data as StatementOrderResult | undefined, {
                amend: true,
              }));
        setAmendError(message);
        toast.error(message);
        return;
      }
      setAmendOrdered(true);
      toast.success('Statement details amended successfully.');
    } catch {
      const message = 'Could not amend the statement details. Please retry.';
      setAmendError(message);
      toast.error(message);
    } finally {
      setAmendSubmitting(false);
    }
  }

  /**
   * CCF-38: order the selected interest certificate year(s). Multiple years can
   * be ordered at once; each is a separate `certificate/order` call carrying the
   * (possibly amended) automated-cert flag. A single failed year surfaces inline.
   */
  async function orderCertificates(): Promise<void> {
    setCertSubmitting(true);
    setCertError('');
    const years = Array.from(certSelected);
    try {
      for (const yearEnding of years) {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/accounts/certificate-order', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber: selectedAccount,
            yearEnding,
            annualCertRequired: certAnnual ? 'Y' : 'N',
          },
        });
        if (
          apiError ||
          !data ||
          (data as { Success?: boolean }).Success !== true
        ) {
          // Issue #248: RBAC action-denial → verbatim inline copy; the Teller-ID
          // 403 keeps its own distinct message.
          const message =
            resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `${yearEnding}: ${formatOrderError(
                  data as StatementOrderResult | undefined,
                  { certificate: true },
                )}`);
          setCertError(message);
          toast.error(message);
          return;
        }
      }
      setCertOrdered(true);
      toast.success('Interest certificate ordered successfully.');
    } catch {
      const message = 'Could not order the interest certificate. Please retry.';
      setCertError(message);
      toast.error(message);
    } finally {
      setCertSubmitting(false);
    }
  }

  /**
   * CCF-38: the automated-interest-cert flag (`AnnualCertRequired`) is carried
   * only on the `certificate/order` gateway op, which ORDERS a certificate for
   * the supplied YearEnding. The R4 contract exposes NO flag-only / amend-only
   * mechanism to persist the preference on its own — so a standalone "Update"
   * cannot be honoured without inventing a gateway field (which we never do).
   *
   * Rather than fabricate a success, we FAIL CLOSED with an honest inline
   * warning beside the button: the setting can only be changed by ordering a
   * certificate. The checkbox still reflects the current flag for DISPLAY (read
   * from `certificates/list`, which IS contract-backed).
   */
  function updateAutomatedCert(): void {
    setCertUpdateError(
      'The automated interest certificate setting cannot be changed on its ' +
        'own via the gateway. Order a certificate below to set it (the ' +
        'automated-cert preference is applied with the order).',
    );
  }

  const selected = accounts.find((a) => a.AccountNumber === selectedAccount);
  const selectedLabel = selected ? accountOptionLabel(selected) : '';

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="statements-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Statements email templates (#704): EDS Data Breach consolidated into
            a single top-right control in the page header, mirroring
            the pattern on Card Maintenance (#472) and Direct Debits (#705).
            This list holds exactly one template, so the shared
            EmailTemplateMenu renders it as a single button, not a dropdown
            (#803). Prefilled best-effort from the in-context customer name and CIF;
            delivered as a formatted downloadable Outlook draft (.eml) or plain-
            text mailto: draft. */}
        <EmailTemplateMenu
          templates={STATEMENTS_TEMPLATES}
          context={{
            customerName: customer.name ?? '',
            cif: customerNumber,
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="statements-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Statements
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Account selection + Show closed accounts (CCF-28). */}
      <div className="mb-6 flex items-start gap-6 flex-wrap">
        <div className="w-[400px]">
          <Dropdown
            id="statement-account"
            label="Select account"
            placeholder="Select account"
            value={selectedAccount}
            onChange={onAccountChange}
            options={visibleAccounts.map((a) => ({
              value: a.AccountNumber,
              // #495/#381: use the shared `<name> <last4>` account-list label
              // (the "Transfer Between Accounts" convention) so this dropdown
              // mirrors every other account-list dropdown (Transactions / Bill
              // Payments / Standing Orders) instead of the old bespoke
              // "Type - last4" format. Nickname-aware, masked to last 4 (CCF-80),
              // and #262-total against a missing AccountNumber.
              label: accountOptionLabel(a),
            }))}
          />
        </div>
        <div className="flex items-center gap-3 mt-10">
          <input
            type="checkbox"
            id="show-closed-accounts"
            checked={showClosed}
            onChange={() => setShowClosed((c) => !c)}
            className="size-[20px] accent-[#FF703E] cursor-pointer"
          />
          <label
            htmlFor="show-closed-accounts"
            className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none whitespace-nowrap"
          >
            Show closed accounts
          </label>
        </div>
      </div>

      {/* Account details (CCF-28): IBAN / available balance / owners / primary. */}
      {selectedAccount && (
        <div className="max-w-[900px] mb-6 border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Account Details
          </h2>
          {detailLoading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
            >
              Loading…
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-1 m-0">
                  IBAN
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  {detail?.Iban ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-1 m-0">
                  Available balance
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  {money(detail?.AvailableBalance, detail?.Currency)}
                </p>
              </div>
              <div>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-1 m-0">
                  Owners
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  {/* CCF-28: real account owners from the detail; fall back to
                      the banner customer name when the gateway omits them. */}
                  {ownersLabel(detail) || customer.name || '—'}
                </p>
              </div>
              <div>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] mb-1 m-0">
                  Primary account holder
                </p>
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                  {/* #459: a Yes / No indicator of whether the identified caller
                      is the PRIMARY holder of this account — sourced from the
                      gateway's per-account `PrimaryAccountHolder` (Y/N) flag,
                      normalised by `flagLabel`. NOT the holder's name (the
                      Owners field above lists ALL holders, #414). Renders "—"
                      when the gateway omits the flag. */}
                  {flagLabel(detail?.PrimaryAccountHolder)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statement options dropdown (CCF-28) — 4 options. */}
      {selectedAccount && !detailLoading && (
        <div className="mb-6 w-[350px]">
          <Dropdown
            id="statement-options"
            label="Statement options"
            placeholder="Select statement option"
            value={option}
            onChange={onOptionChange}
            options={STATEMENT_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
      )}

      {/* Up to date statements (CCF-74). */}
      {option === 'up-to-date' && (
        <div className="max-w-[1000px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Up to date statements
          </h2>

          {/* CIF Address Validation (CCF-74): the agent confirms the CIF address
              and the account's postal/delivery address on file, presented the
              same way as Card Maintenance (CCF-77) via the shared panel —
              labelled CIF (Line 1–5 + Eircode) and Postal (Line 1–5) columns.
              "Change address page" routes to the Change Customer Details screen
              (SCRN-24, `/personal-details`) — per #256 (dylan219), reversing the
              earlier #52/!193 wiring to the postal-address screen. */}
          <CustomerAddressPanel
            cifAddress={cifAddress}
            cifNumber={customerNumber}
            eircode={cifEircode}
            postalAddress={postalAddress}
            changeAddressPath="/personal-details"
          />

          {/* Statement information (CCF-74). */}
          <div className="flex flex-wrap gap-x-12 gap-y-4 mb-8">
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Last Statement Date:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {detail?.LastStatementDate ?? '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Next Statement Date:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {detail?.NextStatementDate ?? '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Statement Frequency:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {frequencyLabel(detail?.StatementFrequency)}
              </span>
            </div>
            {/* CCF-74: E-statement / Green-statement indicators. Read off the
                detail when present, rendered gracefully ("—") when absent. */}
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Signed up to E-Statements:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {flagLabel(detail?.EStatement)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Opted into Green Statements:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {flagLabel(detail?.GreenStatement)}
              </span>
            </div>
          </div>

          {/* Mandatory scripting (CCF-74). */}
          <div className="mb-6 border-l-4 border-[#FF703E] bg-[#F8EEE3] p-4 max-w-[640px]">
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-2 m-0">
              If e-statements are applicable, they will be available online in 3
              working days
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-2 m-0">
              Also received by post within 5 - 7 working days
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
              Ensure to check if the customer you are speaking with is the
              primary account holder, as the statement will be posted to the
              address associated with the primary account holder.
            </p>
          </div>

          {/* Scripting complete (CCF-74) — reveals the Order statement button. */}
          <div className="mb-6 flex items-center gap-3">
            <input
              type="checkbox"
              id="scripting-complete"
              checked={scriptingComplete}
              onChange={() => setScriptingComplete((c) => !c)}
              className="size-[20px] accent-[#FF703E] cursor-pointer"
            />
            <label
              htmlFor="scripting-complete"
              className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
            >
              Scripting complete
            </label>
          </div>

          {scriptingComplete && !ordered && (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={() => void orderStatement()}
                aria-describedby={
                  orderError ? 'statement-order-error' : undefined
                }
                className="self-start px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Working…' : 'Order statement'}
              </button>
              {/* Issue #80: a failed/declined order ALWAYS surfaces a clear,
                  persistent error directly beneath the action button. */}
              {orderError && (
                <div
                  id="statement-order-error"
                  role="alert"
                  aria-live="assertive"
                  className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {orderError}
                </div>
              )}
            </div>
          )}

          {ordered && (
            <div className="py-2" role="alert" aria-live="polite">
              <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
                Statement ordered
              </p>
            </div>
          )}
        </div>
      )}

      {/* Duplicate statements (CCF-75) — date-range order + charges scripting. */}
      {option === 'duplicate' && (
        <div className="max-w-[1000px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Duplicate statements
          </h2>

          {/* CIF + postal address validation (CCF-75) — same panel as CCF-74. */}
          <CustomerAddressPanel
            cifAddress={cifAddress}
            cifNumber={customerNumber}
            eircode={cifEircode}
            postalAddress={postalAddress}
            changeAddressPath="/personal-details"
          />

          {/* Statement information (CCF-75). */}
          <div className="flex flex-wrap gap-x-12 gap-y-4 mb-8">
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Last Statement Date:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {detail?.LastStatementDate ?? '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Next Statement Date:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {detail?.NextStatementDate ?? '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Statement Frequency:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {frequencyLabel(detail?.StatementFrequency)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Signed up to E-Statements:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {flagLabel(detail?.EStatement)}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]">
                Opted into Green Statements:
              </span>
              <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                {flagLabel(detail?.GreenStatement)}
              </span>
            </div>
          </div>

          {/* Date range (CCF-75) — From + To, both mandatory.
              #179: per @dylan219, the agent picks each date from a native
              calendar popup (`<input type="date">`, the same pattern as the
              Amend "Next due date" field, #158). The picker value is ISO
              `YYYY-MM-DD`; we round-trip it to/from the DD/MM/CCYY the gateway
              contract and the validation helpers expect (`isoToDdmmyyyy` /
              `ddmmyyyyToIso`) so the underlying state stays DD/MM/CCYY. The
              picker's `max`/`min` steer the agent away from future dates and a
              backwards range at the source, but the existing client-side
              validation (real calendar date, not in the future, From ≤ To) is
              kept intact and still surfaces an inline error next to the
              offending box (aria-invalid + red border + message beneath) — the
              #80/#82 inline-error UX rule. Validation runs on blur and on
              submit; editing a box clears its own error. */}
          <div className="flex flex-wrap gap-6 mb-6">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="dup-from"
                className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                From date (DD/MM/YYYY)
              </label>
              <input
                id="dup-from"
                type="date"
                max={todayIso()}
                value={ddmmyyyyToIso(dupFrom)}
                aria-invalid={dupFromError ? true : undefined}
                aria-describedby={dupFromError ? 'dup-from-error' : undefined}
                onChange={(e) => {
                  setDupFrom(
                    e.target.value ? isoToDdmmyyyy(e.target.value) : '',
                  );
                  if (dupFromError) setDupFromError('');
                }}
                onBlur={() => validateDupDates()}
                className={`w-[200px] px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white focus:outline-none ${
                  dupFromError
                    ? 'border-[#dc2626] focus:border-[#dc2626]'
                    : 'border-[#dad7d3] focus:border-[#005fcc]'
                }`}
              />
              {dupFromError && (
                <p
                  id="dup-from-error"
                  role="alert"
                  className="w-[200px] text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {dupFromError}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="dup-to"
                className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                To date (DD/MM/YYYY)
              </label>
              <input
                id="dup-to"
                type="date"
                min={ddmmyyyyToIso(dupFrom) || undefined}
                max={todayIso()}
                value={ddmmyyyyToIso(dupTo)}
                aria-invalid={dupToError ? true : undefined}
                aria-describedby={dupToError ? 'dup-to-error' : undefined}
                onChange={(e) => {
                  setDupTo(e.target.value ? isoToDdmmyyyy(e.target.value) : '');
                  if (dupToError) setDupToError('');
                }}
                onBlur={() => validateDupDates()}
                className={`w-[200px] px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white focus:outline-none ${
                  dupToError
                    ? 'border-[#dc2626] focus:border-[#dc2626]'
                    : 'border-[#dad7d3] focus:border-[#005fcc]'
                }`}
              />
              {dupToError && (
                <p
                  id="dup-to-error"
                  role="alert"
                  className="w-[200px] text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {dupToError}
                </p>
              )}
            </div>
          </div>

          {/* Mandatory charges scripting (CCF-75). */}
          <div className="mb-6 border-l-4 border-[#FF703E] bg-[#F8EEE3] p-4 max-w-[640px]">
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-2 m-0">
              Statements should be received by post within 5–7 working days.
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2 m-0">
              Charge applies — €2.50 per page up to a maximum charge of €20.00.
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
              Ensure to check if the customer you are speaking with is the
              primary account holder, as the statement will be posted to the CIF
              address associated with the primary account holder.
            </p>
          </div>

          <div className="mb-6 flex items-center gap-3">
            <input
              type="checkbox"
              id="dup-scripting-complete"
              checked={dupScripting}
              onChange={() => setDupScripting((c) => !c)}
              className="size-[20px] accent-[#FF703E] cursor-pointer"
            />
            <label
              htmlFor="dup-scripting-complete"
              className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
            >
              Scripting complete
            </label>
          </div>

          {dupScripting && !dupOrdered && (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={dupSubmitting || !dupFrom.trim() || !dupTo.trim()}
                onClick={() => void orderDuplicate()}
                aria-describedby={
                  dupError ? 'duplicate-order-error' : undefined
                }
                className="self-start px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dupSubmitting ? 'Working…' : 'Order statement'}
              </button>
              {dupError && (
                <div
                  id="duplicate-order-error"
                  role="alert"
                  aria-live="assertive"
                  className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {dupError}
                </div>
              )}
            </div>
          )}

          {dupOrdered && (
            <div className="py-2" role="alert" aria-live="polite">
              <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
                Duplicate statement ordered
              </p>
            </div>
          )}
        </div>
      )}

      {/* Amend statement details (CCF-29) — pre-filled form. */}
      {option === 'amend' && (
        <div className="max-w-[1000px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Amend statement details
          </h2>

          {amendLoading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
            >
              Loading…
            </p>
          ) : (
            <>
              {/* Pre-filled amendable fields (#86/#87): frequency, next due
                  date, green-statement, EOM. */}
              <div className="flex flex-col gap-6 max-w-[400px] mb-6">
                <Dropdown
                  id="amend-frequency"
                  label="Statement frequency"
                  placeholder="Select frequency"
                  value={amendFrequency}
                  onChange={setAmendFrequency}
                  options={FREQUENCY_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="amend-next-date"
                    className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                  >
                    Next due date
                  </label>
                  <input
                    id="amend-next-date"
                    type="date"
                    min={todayIso()}
                    value={ddmmyyyyToIso(amendNextDate)}
                    disabled={amendEom}
                    onChange={(e) =>
                      setAmendNextDate(
                        e.target.value ? isoToDdmmyyyy(e.target.value) : '',
                      )
                    }
                    className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white focus:outline-none focus:border-[#005fcc] disabled:bg-[#f3f4f6] disabled:text-[#9ca3af] disabled:cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="amend-eom"
                    checked={amendEom}
                    onChange={() => {
                      setAmendEom((c) => {
                        const next = !c;
                        if (next)
                          setAmendNextDate(ddmmyyyyLastDayOfCurrentMonth());
                        return next;
                      });
                    }}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="amend-eom"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Issue statement at end of month (EOM)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="amend-green"
                    checked={amendGreen}
                    onChange={() => setAmendGreen((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="amend-green"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Green statements
                  </label>
                </div>
              </div>

              {!amendOrdered && (
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    disabled={amendSubmitting || !amendDetails}
                    onClick={() => void amendStatement()}
                    aria-describedby={amendError ? 'amend-error' : undefined}
                    className="self-start px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {amendSubmitting ? 'Working…' : 'Amend statement'}
                  </button>
                  {amendError && (
                    <div
                      id="amend-error"
                      role="alert"
                      aria-live="assertive"
                      className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                    >
                      {amendError}
                    </div>
                  )}
                </div>
              )}

              {amendOrdered && (
                <div className="py-2" role="alert" aria-live="polite">
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
                    Statement details amended
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Interest certificate (CCF-38) — automated-cert flag + year picker. */}
      {option === 'interest' && (
        <div className="max-w-[1000px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Interest certificate
          </h2>

          {/* CIF + postal address validation (CCF-38) — same panel as CCF-74. */}
          <CustomerAddressPanel
            cifAddress={cifAddress}
            cifNumber={customerNumber}
            eircode={cifEircode}
            postalAddress={postalAddress}
            changeAddressPath="/personal-details"
          />

          {certLoading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0"
            >
              Loading…
            </p>
          ) : !certList ? (
            // The certificates list failed to load (gateway business error or
            // transport). Surface the gateway Code + Message inline (#80/#111).
            <div
              role="alert"
              aria-live="assertive"
              className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {certError || 'Could not load the interest certificates.'}
            </div>
          ) : (
            <>
              {/* Automated interest certificate (CCF-38) — Y/N + Update. */}
              <div className="mb-6 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="cert-annual"
                    checked={certAnnual}
                    onChange={() => setCertAnnual((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="cert-annual"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Automated interest certificates active
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => updateAutomatedCert()}
                  aria-describedby={
                    certUpdateError ? 'cert-update-error' : undefined
                  }
                  className="self-start px-5 py-2 bg-white border-2 border-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[14px] hover:bg-[#F8EEE3] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </button>
                {/* CCF-38: the gateway has no contract-backed flag-only write, so
                    a standalone Update fails closed with an honest inline notice
                    beside the button (recoverable — order a certificate to set
                    the flag). Never a fabricated success. */}
                {certUpdateError && (
                  <div
                    id="cert-update-error"
                    role="alert"
                    aria-live="polite"
                    className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    {certUpdateError}
                  </div>
                )}
              </div>

              {/* Year picker (CCF-38) — groups of 5 with Next/Previous. */}
              <div className="mb-6">
                <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                  Select year(s)
                </p>
                {(certList?.Certificates ?? []).length === 0 ? (
                  <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                    No certificate years are available for this account.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-col gap-2">
                      {(certList?.Certificates ?? [])
                        .slice(
                          certPage * CERT_PAGE_SIZE,
                          certPage * CERT_PAGE_SIZE + CERT_PAGE_SIZE,
                        )
                        .map((cert) => (
                          <label
                            key={cert.YearEnding}
                            className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                          >
                            <input
                              type="checkbox"
                              checked={certSelected.has(cert.YearEnding)}
                              onChange={() =>
                                setCertSelected((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(cert.YearEnding))
                                    next.delete(cert.YearEnding);
                                  else next.add(cert.YearEnding);
                                  return next;
                                })
                              }
                              className="size-[20px] accent-[#FF703E] cursor-pointer"
                            />
                            <span>Year ending {cert.YearEnding}</span>
                          </label>
                        ))}
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <button
                        type="button"
                        disabled={certPage === 0}
                        onClick={() => setCertPage((p) => Math.max(0, p - 1))}
                        className="px-4 py-2 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[14px] text-[#1d252c] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#979c9f]"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={
                          (certPage + 1) * CERT_PAGE_SIZE >=
                          (certList?.Certificates ?? []).length
                        }
                        onClick={() => setCertPage((p) => p + 1)}
                        className="px-4 py-2 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[14px] text-[#1d252c] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#979c9f]"
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>

              {certSelected.size > 0 && !certOrdered && (
                <div className="flex flex-col gap-3">
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                    Advise the customer of a delivery timeframe of up to 7
                    working days.
                  </p>
                  <button
                    type="button"
                    disabled={certSubmitting}
                    onClick={() => void orderCertificates()}
                    aria-describedby={certError ? 'cert-error' : undefined}
                    className="self-start px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {certSubmitting ? 'Working…' : 'Order interest certificate'}
                  </button>
                  {certError && (
                    <div
                      id="cert-error"
                      role="alert"
                      aria-live="assertive"
                      className="max-w-[640px] p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                    >
                      {certError}
                    </div>
                  )}
                </div>
              )}

              {certOrdered && (
                <div className="py-2" role="alert" aria-live="polite">
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
                    Interest certificate ordered
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Account context line (for clarity when an account is selected). */}
      {selectedAccount && (
        <p className="sr-only">Selected account: {selectedLabel}</p>
      )}
    </main>
  );
}
